/**
 * CombatFeedbackConfig
 *
 * Server→client school mapping + intensity-bucket helper. Visual tuning
 * (colors, tints, crit color, intensity thresholds) has moved to
 * `src/shared/config/combat-feedback.config.ts` (defaults+dev-override
 * pattern per `docs/plans/2026-04-25-CONFIG-MIGRATION.md`).
 *
 * The legacy named exports (`DAMAGE_TYPE_COLORS`, `DAMAGE_TYPE_TINTS`,
 * `CRITICAL_HIT_COLOR`, `IMPACT_INTENSITY`) are re-exported from the new
 * config so existing consumers keep working without edits. Migrate
 * per-file at your leisure to `import { COMBAT_FEEDBACK } from '<path>/
 * shared/config/combat-feedback.config'`.
 */

import { COMBAT_FEEDBACK } from '../config/combat-feedback.config';

/** Server ability school values (h2kdb ABILITY_DATA) */
export type AbilitySchool =
  | 'physical' | 'fire' | 'frost' | 'arcane'
  | 'shadow' | 'holy' | 'nature' | 'chaos' | 'various';

/** Client-side feedback type (accepted by CombatFeedback.triggerImpact).
 *  shadow + nature added 2026-07-16 — their hit defs exist since F5-013, so
 *  the old shadow→arcane / nature→physical collapse (the F5-013 fossil one
 *  layer deeper) no longer has a reason to exist. */
export type FeedbackType = 'physical' | 'fire' | 'ice' | 'lightning' | 'arcane' | 'holy' | 'shadow' | 'nature';

/** @deprecated Import `COMBAT_FEEDBACK.damageTypeColors` from `config/combat-feedback.config`. */
export const DAMAGE_TYPE_COLORS: Record<string, string> = COMBAT_FEEDBACK.damageTypeColors;

/** @deprecated Import `COMBAT_FEEDBACK.damageTypeTints` from `config/combat-feedback.config`. */
export const DAMAGE_TYPE_TINTS: Record<string, number> = COMBAT_FEEDBACK.damageTypeTints;

/** Map server school → client CombatFeedback type. Structural, not visual tuning — stays here. */
export const SCHOOL_TO_FEEDBACK_TYPE: Record<string, FeedbackType> = {
  physical: 'physical',
  fire: 'fire',
  frost: 'ice',
  arcane: 'arcane',
  holy: 'holy',
  shadow: 'shadow',
  nature: 'nature',
  chaos: 'fire',
  lightning: 'lightning',
  various: 'physical',
};

/** @deprecated Import `COMBAT_FEEDBACK.criticalHitColor` from `config/combat-feedback.config`. */
export const CRITICAL_HIT_COLOR = COMBAT_FEEDBACK.criticalHitColor;

/** @deprecated Import `COMBAT_FEEDBACK.impactIntensity` from `config/combat-feedback.config`. */
export const IMPACT_INTENSITY = COMBAT_FEEDBACK.impactIntensity;

/** Resolve intensity label from damage + critical. Helper, stays here. */
export function resolveIntensity(damage: number, critical: boolean): 'light' | 'medium' | 'heavy' | 'massive' {
  if (critical) return damage > IMPACT_INTENSITY.medium ? 'massive' : 'heavy';
  if (damage > IMPACT_INTENSITY.heavy) return 'massive';
  if (damage > IMPACT_INTENSITY.medium) return 'heavy';
  if (damage > IMPACT_INTENSITY.light) return 'medium';
  return 'light';
}
